import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  formatGenerationFullDate,
  formatGenerationTime,
  isGenerationActive,
  recordStatusView,
  type GenerationSummary,
  type GenerationTime,
  type TextRef,
} from '@entities/Generation';
import { cn } from '@shared/lib';
import { ChevronDownIcon, StatusGlyph, StatusSheen } from '@shared/ui';
import { GenerationBreakdown } from './GenerationBreakdown';
import type { ItemActionsContext } from './ItemLine';
import { TONE_CLASS } from './SenseLine';

const RISE: [number, number, number, number] = [0.2, 0.9, 0.2, 1];
const FALL: [number, number, number, number] = [0.4, 0, 1, 1];

export const headerDomId = (id: string) => `gl-h-${id}`;
const bodyDomId = (id: string) => `gl-b-${id}`;

function timeLabel(t: TFunction, time: GenerationTime): string {
  switch (time.kind) {
    case 'now':
      return t('generations.time.now');
    case 'min':
      return t('generations.time.min', { count: time.count });
    case 'hour':
      return t('generations.time.hour', { count: time.count });
    case 'yesterday':
      return t('generations.time.yesterday');
    case 'date':
      return time.text;
  }
}

const text = (t: TFunction, ref: TextRef) => t(ref.key, ref.params);

export interface GenerationRowProps {
  summary: GenerationSummary;
  now: number;
  /** Нажатие этого захода по записи («Создать», «Повторить»): отсчёт «дольше обычного» (§ 22.4). */
  pressedAt: number | null;
  /** Когда этот заход впервые увидел запись (ключ — id) или её элемент (`id:itemId`) в «генерируется».
   *  Стабильная функция секции. */
  firstSeenAt: (key: string) => number;
  open: boolean;
  /** Раскрыть без анимации высоты — по нажатию сигнала (§ 8): прокрутку к записи делает секция. */
  instantOpen: boolean;
  /** Подсветка после нажатия сигнала; новый ключ — подсветить снова. */
  highlightKey: number | null;
  /** Как строка появилась: новая запись сверху въезжает по высоте, «Показать ещё» — проявляется. */
  enter: 'grow' | 'fade' | null;
  /** Ушла (404): высота и прозрачность до 0 за 200 мс, потом запись убирают из списка. */
  leaving: boolean;
  reduced: boolean;
  /** Список уже перечитан в этом заходе. Вспышка «готово» — только после этого: смена, которую принёс
   *  первый ответ после возврата на главную, — не то, что строка «увидела» (§ 22.4). */
  listSettled: boolean;
  /** Ref-колбэк строки записи — для IntersectionObserver списка (`inView`). */
  observe: (el: Element | null) => void;
  ctx: ItemActionsContext;
  onToggle: (summary: GenerationSummary) => void;
  /** «Свернуть» внизу записи: сначала прокрутка к заголовку, если он за верхом окна. */
  onCollapseFromBottom: (id: string) => void;
  /** Раскрытие доехало: если под заголовком видно меньше 160 px — прокрутить (§ 22.5). */
  onExpanded: (header: HTMLElement) => void;
  onGone: (id: string) => void;
  onLeft: (id: string) => void;
}

/**
 * Запись «Недавно добавленных» (ТЗ главной § 22.4, § 22.5): свёрнутая — значок статуса, слово (или «Подбор:
 * тема»), время и «статус → модуль»; раскрывается на месте разрезом. Пока идёт генерация — кольцо, блик и
 * тёплый фон; закончилась на глазах у строки — ✓ вырастает и фон вспыхивает success/10. Удалённая — текст,
 * не кнопка, приглушена и не раскрывается. `memo`: событие о другой записи эту строку не перерисовывает.
 */
export const GenerationRow = memo(function GenerationRow({
  summary,
  now,
  pressedAt,
  firstSeenAt,
  open,
  instantOpen,
  highlightKey,
  enter,
  leaving,
  reduced,
  listSettled,
  observe,
  ctx,
  onToggle,
  onCollapseFromBottom,
  onExpanded,
  onGone,
  onLeft,
}: GenerationRowProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'ru';
  const id = summary.id;
  // «Дольше обычного» (§ 22.4): от нажатия этого захода, иначе — с первого показа «генерируется».
  const slowSince = isGenerationActive(summary) ? (pressedAt ?? firstSeenAt(id)) : null;
  const itemSlowSince = useCallback(
    (itemId: string) => pressedAt ?? firstSeenAt(`${id}:${itemId}`),
    [pressedAt, firstSeenAt, id],
  );
  const view = recordStatusView(summary, { now, slowSince });
  const headerRef = useRef<HTMLElement | null>(null);
  const setHeader = useCallback((el: HTMLElement | null) => {
    headerRef.current = el;
  }, []);

  // Вспышка — только на смене, которую увидела уже показанная строка (прошлый статус — в ref): после
  // возврата на главную и после «Показать ещё» старые записи не вспыхивают. Смена, пришедшая с первой
  // перечиткой списка в этом заходе, — тоже нет: строка показывала устаревший кэш.
  const prevLive = useRef(view.live);
  const settledBefore = useRef(listSettled);
  const [flashKey, setFlashKey] = useState<number | null>(null);
  useEffect(() => {
    const was = prevLive.current;
    prevLive.current = view.live;
    if (was && !view.live && summary.status === 'ready' && settledBefore.current) setFlashKey(Date.now());
  }, [view.live, summary.status]);
  useEffect(() => {
    settledBefore.current = listSettled;
  });
  useEffect(() => {
    if (flashKey === null) return;
    const timer = window.setTimeout(() => setFlashKey(null), 1300);
    return () => window.clearTimeout(timer);
  }, [flashKey]);

  const isTopic = summary.kind === 'topic';
  const title = isTopic ? t('generations.topic', { topic: summary.title }) : summary.title;
  const moduleName = summary.module?.name ?? '';
  const time = timeLabel(t, formatGenerationTime(summary.createdAt, now, lang));
  const aria = t('generations.rowAria', {
    title,
    module: moduleName,
    status: view.aria.map((ref) => text(t, ref)).join(', '),
    date: formatGenerationFullDate(summary.createdAt, now, lang),
  });
  const headerId = headerDomId(id);
  const bodyId = bodyDomId(id);
  const flash = flashKey !== null;

  // «Свернуть» снизу: на время анимации у тела записи overflow-anchor: none (§ 13).
  const [anchorOff, setAnchorOff] = useState(false);
  useEffect(() => {
    if (open) setAnchorOff(false);
  }, [open]);

  const handleToggle = useCallback(() => onToggle(summary), [onToggle, summary]);
  const handleExpanded = useCallback(() => {
    if (headerRef.current) onExpanded(headerRef.current);
  }, [onExpanded]);
  const handleCollapse = useCallback(() => {
    setAnchorOff(true);
    onCollapseFromBottom(id);
  }, [onCollapseFromBottom, id]);
  const handleGone = useCallback(() => onGone(id), [onGone, id]);

  const inner = (
    <>
      <span aria-hidden className="flex h-6 w-6 flex-none items-center justify-center">
        <span key={flash ? `pop-${flashKey}` : 'still'} className={cn('inline-flex', flash && 'gl-pop')}>
          <StatusGlyph kind={view.glyph} muted className="gl-glyph" />
        </span>
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex h-[22px] items-baseline gap-2">
          <span
            lang={isTopic ? undefined : 'en'}
            className="min-w-0 flex-1 truncate font-serif text-[17px] font-medium leading-[22px] text-fg"
          >
            {title}
          </span>
          <span className="flex-none whitespace-nowrap text-[12px] leading-4 text-muted tabular-nums">{time}</span>
        </span>
        <span className="flex h-5 min-w-0 items-baseline whitespace-nowrap text-[14px] leading-5 text-muted">
          <span className={cn('flex-none', TONE_CLASS[view.tone])}>
            {text(t, view.label)}
            {view.tail && <span className="gl-tail">{text(t, view.tail)}</span>}
          </span>
          {moduleName && (
            <>
              <span aria-hidden className="flex-none px-[5px]">
                →
              </span>
              <span className="min-w-0 truncate">{moduleName}</span>
            </>
          )}
        </span>
      </span>
    </>
  );

  // Появление: новая запись сверху — высота 0 → 64 и прозрачность за 240 мс; «Показать ещё» — одной
  // прозрачностью за 160 мс; первая отрисовка — без анимации. Соседи сдвигаются сами — без `layout`.
  const initial = reduced || !enter ? false : enter === 'grow' ? { height: 0, opacity: 0 } : { opacity: 0 };
  const shown = leaving
    ? { height: 0, opacity: 0, transition: { duration: reduced ? 0 : 0.2, ease: FALL } }
    : { height: 'auto', opacity: 1, transition: enter === 'fade' ? { duration: 0.16 } : { duration: 0.24, ease: RISE } };

  return (
    <motion.li
      // «На экране» — вся запись, а не только заголовок 64 px: у длинной раскрытой записи ученик читает
      // тело, когда заголовок уже за верхом окна (§ 8, § 23.4 п. 2).
      ref={observe}
      initial={initial}
      animate={shown}
      exit="exit"
      variants={{
        exit: (instant?: boolean) =>
          instant || reduced
            ? { height: 0, opacity: 0, transition: { duration: 0 } }
            : { height: 0, opacity: 0, transition: { duration: 0.24, ease: FALL } },
      }}
      onAnimationComplete={(definition) => {
        // Доехало «ушла» (высота 0) — убрать запись из списка; конец выхода из списка — уже не нужно.
        if (leaving && definition !== 'exit') onLeft(id);
      }}
      className={cn('gl-row', view.live && 'bg-accent/[0.05]', flash && 'is-flash')}
    >
      {view.live && <StatusSheen />}
      {/* Подсветка после нажатия сигнала: рамка accent/50 гаснет за 600 мс; новый ключ — заново. */}
      {highlightKey !== null && <span key={highlightKey} aria-hidden className="gl-hl" />}
      {view.removed ? (
        <div
          ref={setHeader}
          id={headerId}
          className="gl-head relative flex h-16 w-full items-center gap-2 px-3 py-2.5 opacity-60"
        >
          {inner}
          <span aria-hidden className="w-4 flex-none" />
        </div>
      ) : (
        <button
          ref={setHeader}
          type="button"
          id={headerId}
          aria-expanded={open}
          aria-controls={bodyId}
          aria-label={aria}
          onClick={handleToggle}
          className="gl-head relative flex h-16 w-full items-center gap-2 px-3 py-2.5 text-left"
        >
          {inner}
          <ChevronDownIcon
            className={cn(
              'h-4 w-4 flex-none text-muted transition-transform duration-200 motion-reduce:transition-none',
              open && 'rotate-180',
            )}
          />
        </button>
      )}
      <AnimatePresence initial={false}>
        {open && !view.removed && (
          <RowBody
            key="body"
            id={bodyId}
            headerId={headerId}
            // Без анимации высоты — по нажатию сигнала и при «меньше движения».
            instant={instantOpen || reduced}
            // Место под заголовком проверяем всегда, кроме нажатия сигнала: там прокрутку делает секция.
            checkRoom={!instantOpen}
            reduced={reduced}
            anchorOff={anchorOff}
            onExpanded={handleExpanded}
          >
            <GenerationBreakdown
              summary={summary}
              now={now}
              itemSlowSince={itemSlowSince}
              ctx={ctx}
              onCollapse={handleCollapse}
              onGone={handleGone}
            />
          </RowBody>
        )}
      </AnimatePresence>
    </motion.li>
  );
});

/** Тело записи: высота 0 → авто за 220 мс, содержимое проявляется с задержкой 60 мс; сворачивание — 180 мс.
 *  Без анимации высоты (нажатие сигнала, «меньше движения») — только проявление содержимого (§ 8, § 13). */
function RowBody({
  id,
  headerId,
  instant,
  checkRoom,
  reduced,
  anchorOff,
  onExpanded,
  children,
}: {
  id: string;
  headerId: string;
  instant: boolean;
  /** Раскрытие доехало — проверить место под заголовком (§ 22.5). */
  checkRoom: boolean;
  reduced: boolean;
  anchorOff: boolean;
  onExpanded: () => void;
  children: ReactNode;
}) {
  // Без анимации высоты окончательная высота есть сразу после раскладки: при «меньше движения» правило
  // «видно меньше 160 px — заголовок наверх» тоже действует, только прокрутка мгновенная (§ 13).
  const expandedRef = useRef(onExpanded);
  expandedRef.current = onExpanded;
  useLayoutEffect(() => {
    if (instant && checkRoom) expandedRef.current();
    // Только при раскрытии.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <motion.div
      id={id}
      role="region"
      aria-labelledby={headerId}
      initial={instant ? false : { height: 0 }}
      animate={{ height: 'auto', transition: { duration: 0.22, ease: RISE } }}
      exit={{ height: 0, transition: { duration: reduced ? 0 : 0.18, ease: FALL } }}
      onAnimationComplete={(definition) => {
        if (
          !instant &&
          checkRoom &&
          typeof definition === 'object' &&
          definition &&
          'height' in definition &&
          definition.height === 'auto'
        )
          onExpanded();
      }}
      // Сворачивание снизу: привязка прокрутки браузера вместе с упором в конец страницы дала бы рывок.
      className="overflow-hidden border-t border-solid border-line"
      style={anchorOff ? { overflowAnchor: 'none' } : undefined}
    >
      <motion.div
        initial={{ opacity: reduced ? 1 : 0 }}
        animate={{ opacity: 1, transition: { duration: reduced ? 0 : 0.16, delay: instant ? 0 : 0.06 } }}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}
